import { useHistory } from 'react-router';

import Teapot from 'assets/brand/teapot.png';
import { isMainBankAccountType } from 'common/bankAccount/utils';
import { useCompanyBankAccounts } from 'common/hooks/useCompanyBankAccounts';
import Button from 'components/_core/Button';
import Modal from 'components/_core/Modal';
import SunshineLoader from 'components/_core/SunshineLoader';
import Text from 'components/_core/Text';
import ErrorView from 'components/ErrorView';
import { navigateToBankTransfer } from 'features/Payments/Pages/Transfers/helpers';

import locales from './locales';

interface BalanceNotZeroModalProps {
  isOpen: boolean;
  onClose: () => void;
  bankAccountId: string;
}

const BalanceNotZeroModal = ({
  bankAccountId,
  isOpen,
  onClose,
}: BalanceNotZeroModalProps) => {
  const history = useHistory();
  const { bankAccounts, error, loading } = useCompanyBankAccounts();

  if (loading) {
    return <SunshineLoader />;
  }

  if (error) {
    return <ErrorView />;
  }

  const currentBankAccount = bankAccounts?.find(
    (bankAccount) => bankAccount.bankAccountId === bankAccountId,
  );

  if (!currentBankAccount) {
    throw new Error('Bank account was not found');
  }

  const hasMoreThanOneWallet =
    bankAccounts?.filter(
      (bankAccount) => !isMainBankAccountType(bankAccount.type),
    ).length > 1;

  const handleTransferBalance = () => {
    if (hasMoreThanOneWallet) {
      navigateToBankTransfer(history, {
        bankTransfer: {
          amount: currentBankAccount.nextBalance / 100,
          fromBankAccountId: bankAccountId,
          transferType: 'internal',
        },
        variant: 'closeWallet',
      });
    } else {
      const mainAccount = bankAccounts?.find((bankAccount) =>
        isMainBankAccountType(bankAccount.type),
      );

      if (!mainAccount) {
        throw new Error('Main bank account was not found');
      }

      navigateToBankTransfer(history, {
        bankTransfer: {
          amount: currentBankAccount.nextBalance / 100,
          fromBankAccountId: bankAccountId,
          toBankAccountId: mainAccount.bankAccountId,
          transferType: 'internal',
        },
        variant: 'closeWalletToMainAccount',
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Header />
      <Modal.Body>
        <Modal.Image boxSize="144px" src={Teapot} />
        <Modal.Title>
          {locales.formatString(locales.title, {
            name: currentBankAccount.name,
          })}
        </Modal.Title>
        <Modal.Description>
          <Text>{locales.description}</Text>
        </Modal.Description>
      </Modal.Body>
      <Modal.Footer justifyContent="flex-end">
        <Button icon="arrow-right" onClick={handleTransferBalance}>
          {locales.transferBalance}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BalanceNotZeroModal;
